import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';

interface Props {
    data: any[];
    onAction: (row: any) => void;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.charAt(0) + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const ArchivedResponsibilityCardGrid: React.FC<Props> = ({ data, onAction }) => {
    const intl = useIntl();
    const returnTooltip = intl.formatMessage({ id: 'production.responsibilityArchive.returnAction', defaultMessage: 'Kthe në proces' });

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M2 20h20" /><path d="M5 20V10l7-7 7 7v10" /><path d="M9 20v-6h6v6" />
                    </svg>
                    <p><FormattedMessage id="production.responsibilityArchive.empty" defaultMessage="Nuk u gjet asnjë proces" /></p>
                </div>
            ) : (
                data.map((row, idx) => {
                    const initials = getInitials(row.clientName || '');
                    const avatarColor = getAvatarColor(row.clientName || '');

                    return (
                        <MikaCard
                            key={idx}
                            onClick={() => onAction(row)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{row.responsibilityName || '\u2014'}</span>
                                        <span className="ccg-company">{row.clientName}</span>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#22c55e', background: '#dcfce7' }}>
                                        <FormattedMessage id="production.archive.completed" defaultMessage="E përfunduar" />
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.board.productCol" defaultMessage="Produkti" /></span>
                                            <span className="ccg-info-value">{row.articleName || '\u2014'}</span>
                                        </div>
                                        {row.materialName && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.col.material" defaultMessage="Materiali" /></span>
                                                <span className="ccg-info-value">{row.materialName}</span>
                                            </div>
                                        )}
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.user" defaultMessage="Përdoruesi" /></span>
                                            <span className="ccg-info-value">{row.userName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.duration" defaultMessage="Kohëzgjatja" /></span>
                                            <span className="ccg-info-value" style={{ fontFamily: 'monospace' }}>{row.duration || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.responsibilityArchive.card.department" defaultMessage="Reparti" /></span>
                                            <span className="ccg-info-value">{row.departmentName || '\u2014'}</span>
                                        </div>
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date" style={{ color: '#64748b', fontSize: 11 }}>
                                        {row.trackersEnd || ''}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onAction(row); }} title={returnTooltip}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default ArchivedResponsibilityCardGrid;
